import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import type { Editor } from "@tiptap/react";
import type { CitationMode } from "@/lib/tiptap/extensions/citation";

interface EditorState {
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isStrike: boolean;
  isCode: boolean;
  isHighlight: boolean;
  canUndo: boolean;
  canRedo: boolean;
}

interface EditorContextType {
  activeEditor: Editor | null;
  setActiveEditor: (editor: Editor | null) => void;
  // Formatting commands
  toggleBold: () => void;
  toggleItalic: () => void;
  toggleUnderline: () => void;
  toggleStrike: () => void;
  toggleCode: () => void;
  toggleHighlight: () => void;
  undo: () => void;
  redo: () => void;
  // Insert commands
  insertCitation: (citeKey: string, mode?: CitationMode) => void;
  insertReference: (label: string) => void;
  insertText: (text: string) => void;
  insertFootnote: (text: string) => void;
  insertInlineMath: (latex?: string) => void;
  // Active states
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isStrike: boolean;
  isCode: boolean;
  isHighlight: boolean;
  canUndo: boolean;
  canRedo: boolean;
}

const EditorContext = createContext<EditorContextType | null>(null);

export function EditorProvider({ children }: { children: ReactNode }) {
  const [activeEditor, setActiveEditor] = useState<Editor | null>(null);
  const [editorState, setEditorState] = useState<EditorState>({
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isStrike: false,
    isCode: false,
    isHighlight: false,
    canUndo: false,
    canRedo: false,
  });

  // Update state when editor changes or selection/content updates
  useEffect(() => {
    if (!activeEditor) {
      setEditorState({
        isBold: false,
        isItalic: false,
        isUnderline: false,
        isStrike: false,
        isCode: false,
        isHighlight: false,
        canUndo: false,
        canRedo: false,
      });
      return;
    }

    const updateState = () => {
      setEditorState({
        isBold: activeEditor.isActive("bold"),
        isItalic: activeEditor.isActive("italic"),
        isUnderline: activeEditor.isActive("underline"),
        isStrike: activeEditor.isActive("strike"),
        isCode: activeEditor.isActive("code"),
        isHighlight: activeEditor.isActive("highlight"),
        canUndo: activeEditor.can().undo(),
        canRedo: activeEditor.can().redo(),
      });
    };

    // Initial state
    updateState();

    // Listen for selection and transaction updates
    activeEditor.on("selectionUpdate", updateState);
    activeEditor.on("transaction", updateState);

    return () => {
      activeEditor.off("selectionUpdate", updateState);
      activeEditor.off("transaction", updateState);
    };
  }, [activeEditor]);

  const toggleBold = useCallback(() => {
    activeEditor?.chain().focus().toggleBold().run();
  }, [activeEditor]);

  const toggleItalic = useCallback(() => {
    activeEditor?.chain().focus().toggleItalic().run();
  }, [activeEditor]);

  const toggleUnderline = useCallback(() => {
    activeEditor?.chain().focus().toggleUnderline().run();
  }, [activeEditor]);

  const toggleStrike = useCallback(() => {
    activeEditor?.chain().focus().toggleStrike().run();
  }, [activeEditor]);

  const toggleCode = useCallback(() => {
    activeEditor?.chain().focus().toggleCode().run();
  }, [activeEditor]);

  const toggleHighlight = useCallback(() => {
    // 'highlight' mark is registered in the schema (ParagraphBlock uses it);
    // toggleMark is generic so we don't depend on the Highlight extension's
    // named command being present.
    activeEditor?.chain().focus().toggleMark("highlight").run();
  }, [activeEditor]);

  const undo = useCallback(() => {
    activeEditor?.chain().focus().undo().run();
  }, [activeEditor]);

  const redo = useCallback(() => {
    activeEditor?.chain().focus().redo().run();
  }, [activeEditor]);

  const insertCitation = useCallback((citeKey: string, mode: CitationMode = "parenthetical") => {
    if (!activeEditor) return;
    // Insert a structured citation node carrying its mode (→ \citep / \citet
    // / \citeauthor / \citeyear on export). Parenthetical is the default.
    activeEditor.chain().focus().insertContent({
      type: "citation",
      attrs: { citeKey, mode },
    }).run();
  }, [activeEditor]);

  const insertReference = useCallback((label: string) => {
    if (!activeEditor) return;
    // Insert @ref{label} which will be rendered by the Reference extension
    activeEditor.chain().focus().insertContent(`@ref{${label}}`).run();
  }, [activeEditor]);

  const insertText = useCallback((text: string) => {
    if (!activeEditor) return;
    activeEditor.chain().focus().insertContent(text).run();
  }, [activeEditor]);

  const insertFootnote = useCallback((text: string) => {
    if (!activeEditor) return;
    activeEditor.chain().focus().insertFootnote(text).run();
  }, [activeEditor]);

  const insertInlineMath = useCallback((latex?: string) => {
    if (!activeEditor) return;
    activeEditor.chain().focus().insertContent(`$${latex ?? "E=mc^2"}$`).run();
  }, [activeEditor]);

  return (
    <EditorContext.Provider
      value={{
        activeEditor,
        setActiveEditor,
        toggleBold,
        toggleItalic,
        toggleUnderline,
        toggleStrike,
        toggleCode,
        toggleHighlight,
        undo,
        redo,
        insertCitation,
        insertReference,
        insertText,
        insertFootnote,
        insertInlineMath,
        ...editorState,
      }}
    >
      {children}
    </EditorContext.Provider>
  );
}

export function useEditorContext() {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error("useEditorContext must be used within an EditorProvider");
  }
  return context;
}

/**
 * Non-throwing variant — returns null when there's no EditorProvider
 * above this component. Use it from components that legitimately
 * render in *both* the editor (where the provider exists) and other
 * surfaces like Studio (where it doesn't). The PackagesModal does
 * exactly this: in the editor it inserts at the cursor; in Studio
 * it's purely browse-only.
 */
export function useOptionalEditorContext() {
  return useContext(EditorContext);
}
